/**
 * Errors Documentation
 *
 * Error types, common causes, and how to fix them.
 */
import type { DocPage } from '../types';

export const errorsDocs: DocPage[] = [
  {
    id: 'errors/syntax',
    title: 'Syntax Errors',
    category: 'errors',
    tags: ['syntax', 'parse', 'ast', 'error', 'SyntaxError', 'code'],
    version: '2.0',
    related: ['errors/contract', 'api/lifecycle/define'],
    order: 1,
    description: 'JavaScript syntax errors and how to fix them',
    content: `# Syntax Errors

Syntax errors are caught when your code is parsed before execution. The strategy will not run until all syntax errors are fixed.

## Common Causes

### Missing or Extra Brackets

\`\`\`javascript
// Missing closing brace
function onBar(ctx, i) {
  if (ctx.series.close[i] > 100) {
    ctx.order.market('ASSET', 1);
  // <-- missing }
}
\`\`\`

**Fix**: Match every \`{\` with a \`}\`, every \`(\` with a \`)\`, and every \`[\` with a \`]\`. The editor highlights bracket pairs to help.

### Missing Semicolons or Commas

\`\`\`javascript
ctx.indicator('sma', 'SMA', {
  period: 20    // <-- missing comma
  source: 'close'
});
\`\`\`

### Invalid JavaScript

\`\`\`javascript
// Using Python syntax
if close > 100:     // <-- not valid JavaScript
  buy()

// Correct JavaScript
if (ctx.series.close[i] > 100) {
  ctx.order.market('ASSET', 1);
}
\`\`\`

## How to Fix

1. Read the error message — it usually points to the line number
2. Check for unmatched brackets near the indicated line
3. Look for missing commas in object literals and function arguments
4. Use the Monaco editor's syntax highlighting — red underlines indicate problems
`,
  },
  {
    id: 'errors/contract',
    title: 'Contract Errors',
    category: 'errors',
    tags: ['contract', 'missing', 'define', 'init', 'onBar', 'lifecycle', 'required'],
    version: '2.0',
    related: ['api/lifecycle/define', 'api/lifecycle/init', 'api/lifecycle/onBar', 'errors/syntax'],
    order: 2,
    description: 'Missing required lifecycle functions',
    content: `# Contract Errors

Every QSL strategy must export specific lifecycle functions. Contract errors are raised when required functions are missing.

## Required Functions

| Function | Status | Purpose |
|----------|--------|---------|
| \`define(ctx)\` | **Required** | Declare parameters |
| \`init(ctx)\` | Recommended | Set up indicators and state |
| \`onBar(ctx, i)\` | **Required** | Process each candle |

## Error: missing_define

\`\`\`
Error: Strategy must export a define() function
\`\`\`

**Cause**: Your code does not contain a \`function define(ctx)\` declaration.

**Fix**: Add a \`define()\` function, even if you have no parameters:

\`\`\`javascript
function define(ctx) {
  // No parameters needed for this strategy
}
\`\`\`

## Error: missing_onbar

\`\`\`
Error: Strategy must export an onBar() function
\`\`\`

**Cause**: Your code does not contain a \`function onBar(ctx, i)\` declaration.

**Fix**: Add your trading logic inside \`onBar()\`:

\`\`\`javascript
function onBar(ctx, i) {
  // Your trading logic here
}
\`\`\`

## Warning: missing_init

\`\`\`
Warning: Strategy does not export an init() function
\`\`\`

\`init()\` is not strictly required, but without it you cannot declare indicators or initialise state. Most strategies need it.

\`\`\`javascript
function init(ctx) {
  ctx.indicator('sma', 'SMA', { period: ctx.p.period });
}
\`\`\`

## Function Naming

Functions must use these exact names. Common mistakes:

\`\`\`javascript
// Wrong — these will NOT be detected
function Define(ctx) { }     // uppercase D
function on_bar(ctx, i) { }  // underscore
function setup(ctx) { }      // wrong name
const onBar = (ctx, i) => {} // arrow function (may not be detected)

// Correct
function define(ctx) { }
function init(ctx) { }
function onBar(ctx, i) { }
\`\`\`
`,
  },
  {
    id: 'errors/params',
    title: 'Parameter Errors',
    category: 'errors',
    tags: ['parameter', 'param', 'invalid', 'type', 'default', 'min', 'max', 'step', 'validation'],
    version: '2.0',
    related: ['params/defining', 'params/optimization', 'errors/contract'],
    order: 3,
    description: 'Invalid parameter declarations and how to fix them',
    content: `# Parameter Errors

Parameter errors occur when \`ctx.param()\` is called with invalid options.

## Error: invalid_param_type

\`\`\`
Error: Invalid parameter type "number" for param "period". Expected: int, float, string, boolean
\`\`\`

**Cause**: The \`type\` field must be one of the four QSL types.

**Fix**: Use the correct type name:

\`\`\`javascript
// Wrong
ctx.param('period', { type: 'number', default: 14 });
ctx.param('period', { type: 'integer', default: 14 });

// Correct
ctx.param('period', { type: 'int', default: 14 });
ctx.param('threshold', { type: 'float', default: 0.5 });
ctx.param('mode', { type: 'string', default: 'fast' });
ctx.param('useFiler', { type: 'boolean', default: true });
\`\`\`

## Error: missing_default

\`\`\`
Error: Parameter "period" must have a default value
\`\`\`

**Cause**: Every parameter must specify a \`default\` value.

**Fix**:

\`\`\`javascript
// Wrong — no default
ctx.param('period', { type: 'int', min: 5, max: 50 });

// Correct
ctx.param('period', { type: 'int', default: 14, min: 5, max: 50 });
\`\`\`

## Error: invalid_param (range issues)

\`\`\`
Error: Parameter "period": default (100) is outside range [5, 50]
\`\`\`

**Cause**: The \`default\` value must be between \`min\` and \`max\`.

**Fix**: Ensure \`min <= default <= max\`:

\`\`\`javascript
ctx.param('period', {
  type: 'int',
  default: 14,   // Must be between min and max
  min: 5,
  max: 50,
  step: 1,
});
\`\`\`

## Error: Step Size Issues

Common problems with \`step\`:

\`\`\`javascript
// Step must be positive
ctx.param('period', { type: 'int', default: 14, min: 5, max: 50, step: 0 });

// Step should evenly divide the range (or close to it)
// This creates 45/7 ≈ 6.4 steps — some values will be skipped
ctx.param('period', { type: 'int', default: 14, min: 5, max: 50, step: 7 });

// Better — step divides evenly
ctx.param('period', { type: 'int', default: 15, min: 5, max: 50, step: 5 });
\`\`\`

## Optimization Grid Overflows

If your parameters produce more than 50,000 combinations, optimization will not start. Reduce the grid by:

- Increasing step sizes
- Narrowing min/max ranges
- Setting \`optimize: false\` on less important parameters
`,
  },
  {
    id: 'errors/sandbox',
    title: 'Sandbox Restrictions',
    category: 'errors',
    tags: ['sandbox', 'forbidden', 'security', 'restricted', 'eval', 'require', 'fetch', 'setTimeout', 'import'],
    version: '2.0',
    related: ['errors/runtime', 'errors/syntax'],
    order: 4,
    description: 'Forbidden APIs and restricted code patterns',
    content: `# Sandbox Restrictions

QSL strategies run in a secure sandbox. Certain JavaScript APIs are blocked to ensure strategies are deterministic, safe, and cannot access external resources.

## Forbidden Global APIs

The following identifiers are blocked at the AST level — using them anywhere in your code triggers an immediate error:

| Blocked API | Reason |
|-------------|--------|
| \`eval\`, \`Function\` | Code injection risk |
| \`require\`, \`import\` | Module loading blocked |
| \`fetch\`, \`XMLHttpRequest\` | Network access blocked |
| \`setTimeout\`, \`setInterval\`, \`setImmediate\` | Async execution blocked |
| \`clearTimeout\`, \`clearInterval\` | Timer manipulation blocked |
| \`process\`, \`global\`, \`globalThis\` | System access blocked |
| \`Proxy\`, \`Reflect\` | Metaprogramming blocked |
| \`WebSocket\` | Network access blocked |
| \`localStorage\`, \`sessionStorage\` | Browser storage blocked |
| \`document\`, \`window\`, \`navigator\` | DOM access blocked |
| \`__dirname\`, \`__filename\` | File system info blocked |
| \`Buffer\` | Binary data blocked |
| \`SharedArrayBuffer\` | Shared memory blocked |

## Non-Deterministic Code

Strategies must produce identical results on every run with the same inputs. These patterns are blocked:

\`\`\`javascript
// Blocked — random values
Math.random();

// Blocked — current time dependency
new Date();
Date.now();

// Blocked — non-deterministic iteration order
for (let key in obj) { }
\`\`\`

## Control Flow Restrictions

\`\`\`javascript
// Blocked — potential infinite loops
while (condition) { }
do { } while (condition);

// Blocked — recursion (function calling itself)
function calculate(n) {
  return calculate(n - 1);  // recursion not allowed
}

// Allowed — for loops are permitted
for (let j = 0; j < 10; j++) { }
\`\`\`

## What You CAN Use

| Available | Examples |
|-----------|---------|
| **Math** (except \`Math.random\`) | \`Math.abs()\`, \`Math.max()\`, \`Math.min()\`, \`Math.floor()\`, \`Math.log()\`, \`Math.sqrt()\` |
| **Arrays** | \`[].push()\`, \`[].map()\`, \`[].filter()\`, \`[].reduce()\`, \`[].slice()\` |
| **Objects** | \`{}\`, \`Object.keys()\`, \`Object.values()\` |
| **Primitive operations** | Arithmetic, comparisons, string operations |
| **for loops** | \`for (let i = 0; ...)\`, \`for (const x of arr)\` |
| **ctx and q** | Full strategy API and helper functions |

## If You See a Sandbox Error

1. Check the error message — it names the forbidden API
2. Remove or replace the blocked call
3. Use \`ctx.state\` to store values between bars instead of closures or globals
4. Use \`q\` helper functions instead of implementing custom math
`,
  },
  {
    id: 'errors/runtime',
    title: 'Runtime Errors',
    category: 'errors',
    tags: ['runtime', 'TypeError', 'ReferenceError', 'undefined', 'NaN', 'crash', 'debug'],
    version: '2.0',
    related: ['errors/sandbox', 'types/contexts', 'types/helpers'],
    order: 5,
    description: 'Errors that occur during strategy execution',
    content: `# Runtime Errors

Runtime errors happen after your code passes validation but fails during execution. The backtest stops and the error is reported with a stack trace.

## TypeError: Cannot read properties of undefined

\`\`\`
TypeError: Cannot read properties of undefined (reading '14')
\`\`\`

**Common cause**: Accessing an indicator that was not declared in \`init()\`.

\`\`\`javascript
function init(ctx) {
  ctx.indicator('sma', 'SMA', { period: 20 });
}

function onBar(ctx, i) {
  // Wrong — 'ema' was never declared
  const val = ctx.ind.ema[i];  // TypeError: ctx.ind.ema is undefined

  // Correct — use the name from init()
  const val = ctx.ind.sma[i];
}
\`\`\`

## ReferenceError: variable is not defined

\`\`\`
ReferenceError: threshold is not defined
\`\`\`

**Common cause**: Using a parameter name directly instead of \`ctx.p.name\`.

\`\`\`javascript
// Wrong — bare variable
if (ctx.ind.rsi[i] < threshold) { }

// Correct — access via ctx.p
if (ctx.ind.rsi[i] < ctx.p.threshold) { }
\`\`\`

## NaN Results

Your strategy runs but produces no trades or strange values.

**Common cause**: Using an indicator before it has enough data.

\`\`\`javascript
function onBar(ctx, i) {
  // An SMA(20) is NaN for the first 19 bars
  // Always guard against NaN
  if (q.isNaN(ctx.ind.sma[i])) return;

  // Now safe to use
  if (ctx.series.close[i] > ctx.ind.sma[i]) {
    ctx.order.market('ASSET', 1);
  }
}
\`\`\`

## No Trades Generated

Your strategy runs without errors but produces zero trades.

**Checklist**:

1. **Are conditions too strict?** — Print intermediate values or simplify the logic
2. **Is the guard too broad?** — If you return early on NaN, make sure you only check the indicators that matter
3. **Wrong signal direction?** — In LONG_ONLY mode, short signals are ignored
4. **Already in position?** — Check if you're trying to enter when already holding
5. **Date range too short?** — Ensure enough bars for indicators to warm up

## Dry Run Validation

When you save a strategy, the platform runs a **dry run** — a short execution against a small sample of candles. If the dry run crashes, you will see the runtime error before submitting a full backtest. Fix the error shown in the dry run output.

## Debugging Tips

- Start with a minimal strategy and add complexity gradually
- Use \`ctx.state\` to track intermediate values you can inspect in trade metadata
- Add signal metadata: \`ctx.order.market('ASSET', 1, { signal: 'buy', rsi: ctx.ind.rsi[i] })\`
- Test with a narrow date range first to iterate quickly
`,
  },
];
